import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  Container, Row, Col, Card, CardBody, Alert, Badge, Button
} from 'reactstrap';
import MaterialTable from '@material-table/core';
import axios from 'axios';
import swal from 'sweetalert';
import moment from 'moment';

import backend_url from "../../../routes/API_URL";
import Loader from "../../../components/Loader";

const API_URL = backend_url['api_url'];

class AuditTrails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      AuditTrails: [],
      bearer_auth: window.sessionStorage.getItem('token'),
      loading: true,
      isFetching: false,
      error: null,
      page: 0,
      pageSize: 50,
      totalCount: 0,
      searchText: '',
      rawResponse: null
    };
  }

  // Model type to human-readable mapping
  modelLabels = {
    'App\\Region': 'Region',
    'App\\Division': 'Division',
    'App\\District': 'District',
    'App\\Sector': 'Sector',
    'App\\Zone': 'Zone/Cluster',
    'App\\School': 'School',
    'App\\KeyEvidence': 'Key Evidence',
    'App\\Recommendation': 'Recommendation',
    'App\\NesNotification': 'NES Notification',
    'App\\SchoolVisit': 'Inspection Visit',
    'App\\User': 'User Account',
    'App\\Department': 'Department',
    'App\\NationalStandard': 'National Standard',
    'App\\GoodPractice': 'Good Practice',
    'App\\Strength': 'Strength',
    'App\\Weakness': 'Weakness',
    'App\\CriticalIssue': 'Critical Issue',
    'App\\ActionPlan': 'Action Plan',
    'App\\NesLevelAchievement': 'NES Achievement'
  };

  // Event type to human-readable mapping
  eventLabels = {
    'created': 'Created',
    'updated': 'Updated',
    'deleted': 'Deleted',
    'restored': 'Restored'
  };

  // Fetch data with server-side pagination
  fetchData = (page = 0, pageSize = this.state.pageSize, search = '') => {
    return new Promise((resolve, reject) => {
      this.setState({ isFetching: true, error: null });

      const params = {
        page: page + 1,
        per_page: pageSize,
        search: search
      };

      axios.get(API_URL + '/audits', {
        headers: { 
          "Authorization": `Bearer ${this.state.bearer_auth}`,
          "Accept": "application/json"
        },
        params: params
      })
      .then(response => {
        const data = response.data;
        
        if (data.success === false) {
          throw new Error(data.message || 'Failed to fetch audits');
        }
        
        const auditData = data.data || [];
        const totalCount = data.total || 0;
        
        this.setState({
          AuditTrails: auditData,
          totalCount: totalCount,
          page: page,
          pageSize: pageSize,
          loading: false,
          isFetching: false,
          rawResponse: data
        }, () => {
          resolve({
            data: auditData,
            page: page,
            totalCount: totalCount
          });
        });
        
      })
      .catch(error => {
        console.error('Error fetching audit data:', error);
        this.handleApiError(error);
        reject(error);
      });
    });
  }

  // Get human-readable model name
  getModelLabel = (modelType) => {
    return this.modelLabels[modelType] || modelType.replace('App\\', '');
  }

  // Get event badge color
  getEventColor = (eventType) => {
    switch(eventType) {
      case 'created': return 'success';
      case 'updated': return 'warning';
      case 'deleted': return 'danger';
      default: return 'info';
    }
  }

  // Format audit description
  formatAuditDescription = (audit) => {
    if (!audit) return 'N/A';
    
    const userName = audit.user ? 
      `${audit.user.first_name || ''} ${audit.user.last_name || ''}`.trim() : 
      'System User';
    
    const userRole = audit.user?.user_role ? ` (${audit.user.user_role})` : '';
    const modelName = this.getModelLabel(audit.auditable_type);
    const event = this.eventLabels[audit.event] || audit.event;
    
    // Extract key information from new_values
    let details = '';
    if (audit.new_values) {
      const newVals = audit.new_values;
      if (newVals.region_name) details = `"${newVals.region_name}"`;
      else if (newVals.division_name) details = `"${newVals.division_name}"`;
      else if (newVals.district_name) details = `"${newVals.district_name}"`;
      else if (newVals.school_name) details = `"${newVals.school_name}"`;
      else if (newVals.zone_name) details = `"${newVals.zone_name}"`;
      else if (newVals.sector_name) details = `"${newVals.sector_name}"`;
      else if (newVals.recommendation_description) details = `"${newVals.recommendation_description.substring(0, 50)}..."`;
    }
    
    return `${userName}${userRole} ${event} ${modelName} ${details}`.trim();
  }

  // Handle API errors
  handleApiError = (error) => {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        "Failed to load audit trails";
    
    this.setState({ 
      loading: false, 
      isFetching: false, 
      error: errorMessage 
    });

    if (error.response?.status === 401) {
      swal({
        title: "",
        text: "Your session has expired. You'll automatically be logged out within seconds.",
        icon: "warning",
        timer: 2000
      }).then(() => {
        window.sessionStorage.clear();
        window.location.reload();
      });
    } else {
      swal({
        title: "Error",
        text: errorMessage,
        icon: "error",
        button: true
      });
    }
  }

  // View details modal
  viewDetails = (audit) => {
    const details = `
User: ${audit.user ? `${audit.user.first_name} ${audit.user.last_name} (${audit.user.user_role})` : 'System'}
Action: ${this.eventLabels[audit.event] || audit.event}
Resource: ${this.getModelLabel(audit.auditable_type)}
Resource ID: ${audit.auditable_id}
Timestamp: ${moment(audit.created_at).format('YYYY-MM-DD HH:mm:ss')}
URL: ${audit.url || 'N/A'}
IP Address: ${audit.ip_address || 'N/A'}
User Agent: ${audit.user_agent || 'N/A'}

Old Values:
${JSON.stringify(audit.old_values, null, 2) || 'No previous values'}

New Values:
${JSON.stringify(audit.new_values, null, 2) || 'No new values'}
    `;
    
    swal({
      title: "Audit Details",
      text: details,
      icon: "info",
      content: document.createElement('pre'),
      buttons: {
        close: {
          text: "Close",
          value: "close"
        }
      }
    });
  }

  componentDidMount() {
    // Initial fetch
    this.fetchData();
  }

  render() {
    const { AuditTrails, loading, isFetching, error, totalCount } = this.state;

    const columns = [
      {
        title: 'ID',
        field: 'id',
        width: 70,
        render: rowData => (
          <small className="text-muted">#{rowData.id}</small>
        )
      },
      {
        title: 'Timestamp',
        field: 'created_at',
        width: 150,
        render: rowData => (
          <div>
            <div>{moment(rowData.created_at).format('MMM D, YYYY')}</div>
            <small className="text-muted">{moment(rowData.created_at).format('h:mm A')}</small>
          </div>
        )
      },
      {
        title: 'User',
        field: 'user',
        width: 160,
        render: rowData => {
          if (!rowData.user) return 'System';
          return (
            <div>
              <div>
                <strong>{rowData.user.first_name} {rowData.user.last_name}</strong>
              </div>
              <small className="text-muted">{rowData.user.user_role}</small>
            </div>
          );
        }
      },
      {
        title: 'Action',
        field: 'event',
        width: 100,
        render: rowData => (
          <Badge color={this.getEventColor(rowData.event)} className="px-2 py-1">
            {this.eventLabels[rowData.event] || rowData.event}
          </Badge>
        )
      },
      {
        title: 'Resource',
        field: 'auditable_type',
        width: 140,
        render: rowData => this.getModelLabel(rowData.auditable_type)
      },
      {
        title: 'Description',
        field: 'description',
        render: rowData => this.formatAuditDescription(rowData)
      },
      {
        title: 'Details',
        field: 'details',
        width: 80,
        export: false,
        sorting: false,
        render: rowData => (
          <Button
            color="link"
            size="sm"
            onClick={() => this.viewDetails(rowData)}
            title="View Details"
          >
            <i className="mdi mdi-eye-outline"></i>
          </Button>
        )
      }
    ];

    return (
      <Container fluid>
        {loading && <Loader />}
        
        {/* Page Header */}
        <Row className="align-items-center mb-4">
          <Col sm={6}>
            <div className="page-title-box">
              <h4 className="page-title mb-0">
                <i className="mdi mdi-history mr-2"></i>
                Audit Trails
              </h4>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item active">Audit Trails</li>
              </ol>
            </div>
          </Col>
          <Col sm={6} className="text-right">
            <div className="badge badge-light p-2">
              <i className="mdi mdi-database mr-1"></i>
              {totalCount.toLocaleString()} Total Records
            </div>
          </Col>
        </Row>

        {error && (
          <Row className="mb-3">
            <Col>
              <Alert color="danger">
                <h5 className="alert-heading">Error Loading Data</h5>
                <p>{error}</p>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Main Table */}
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">System Activity Log</h5>
                  <div className="d-flex">
                    <Badge color="success" className="mr-2 px-2">Created</Badge>
                    <Badge color="warning" className="mr-2 px-2">Updated</Badge>
                    <Badge color="danger" className="px-2">Deleted</Badge>
                  </div>
                </div>
                
                <p className="text-muted mb-4">
                  Track all system activities including user actions, data changes, and system events.
                </p>
                
                {/* Material Table */}
                <MaterialTable
                  title="Audit Trails"
                  columns={columns}
                  data={query => {
                    return new Promise((resolve, reject) => {
                      const page = query.page;
                      const pageSize = query.pageSize;
                      const search = query.search || '';
                      
                      this.fetchData(page, pageSize, search)
                        .then(() => {
                          resolve({
                            data: this.state.AuditTrails,
                            page: this.state.page,
                            totalCount: this.state.totalCount
                          });
                        })
                        .catch(error => {
                          reject(error);
                        });
                    });
                  }}
                  isLoading={isFetching}
                  options={{
                    exportButton: {
                      csv: true,
                      pdf: false
                    },
                    exportFileName: `audit-logs-${moment().format('YYYY-MM-DD')}`,
                    tableLayout: 'auto',
                    loadingType: 'linear',
                    pageSize: this.state.pageSize,
                    pageSizeOptions: [10, 25, 50, 100],
                    emptyRowsWhenPaging: false,
                    columnsButton: true,
                    exportAllData: false,
                    actionsColumnIndex: -1,
                    search: true,                    
                    sorting: true,
                    debounceInterval: 500,
                    showFirstLastPageButtons: true,
                    paginationType: 'normal',
                    padding: 'dense',
                    rowStyle: { 
                      fontSize: '0.9rem'
                    },
                    headerStyle: {
                      backgroundColor: '#f8f9fa',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    },
                    paging: true,
                    serverSide: true,
                    pagingPosition: 'bottom'
                  }}
                  localization={{
                    body: {
                      emptyDataSourceMessage: 'No audit logs found. Start using the system to see activity logs.',
                      addTooltip: 'Add',
                      deleteTooltip: 'Delete',
                      editTooltip: 'Edit',
                      filterRow: {
                        filterTooltip: 'Filter'
                      }
                    },
                    pagination: {
                      labelRowsSelect: 'rows per page',
                      labelDisplayedRows: '{from}-{to} of {count}',
                      firstTooltip: 'First Page',
                      previousTooltip: 'Previous Page',
                      nextTooltip: 'Next Page',
                      lastTooltip: 'Last Page'
                    },
                    toolbar: {
                      searchTooltip: 'Search audit logs',
                      searchPlaceholder: 'Search by user, action, or resource...',
                      exportTitle: 'Export',
                      exportName: 'Export as CSV',
                      exportCSVName: 'Export as CSV'
                    },
                    header: {
                      actions: 'Details'
                    }
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Stats Card */}
        <Row className="mt-3">
          <Col>
            <Card className="border">
              <CardBody className="py-3">
                <Row>
                  <Col md={3} className="text-center border-right">
                    <h4 className="mb-1 text-primary">{totalCount.toLocaleString()}</h4>
                    <small className="text-muted">Total Records</small>
                  </Col>
                  <Col md={3} className="text-center border-right">
                    <h4 className="mb-1 text-success">64,426</h4>
                    <small className="text-muted">System Total</small>
                  </Col>
                  <Col md={3} className="text-center border-right">
                    <h4 className="mb-1 text-info">{this.state.pageSize}</h4>
                    <small className="text-muted">Current Page Size</small>
                  </Col>
                  <Col md={3} className="text-center">
                    <h4 className="mb-1 text-warning">
                      {this.state.rawResponse ? Math.ceil(this.state.rawResponse.total / this.state.rawResponse.per_page) : 0}
                    </h4>
                    <small className="text-muted">Total Pages</small>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default AuditTrails;